import { useEffect, useState } from "react";
import {
  Box, Stack, Typography, Chip, Alert, CircularProgress, IconButton, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Switch, FormControlLabel,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import RunsView from "./RunsView";
import BoltIcon from "@mui/icons-material/Bolt";
import KeyIcon from "@mui/icons-material/Key";
import {
  listAlerts, BindAlert, AlertLevel, getCronState, setCronState, setCronRate, CronState,
  CRON_RATE_OPTIONS, runScraperNow, getUptime, Uptime,
  getScraperCreds, updateScraperCreds, ScraperCredsState,
} from "../services/reconciliations";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function LevelChip({ level }: { level: AlertLevel }) {
  const colors: Record<AlertLevel, [string, string, string]> = {
    error:   ["#B91C1C", "#FEE2E2", "Error"],
    warning: ["#9A6700", "#FFF6E0", "Warning"],
    info:    ["#1E40AF", "#DBEAFE", "Info"],
  };
  const [fg, bg, label] = colors[level] ?? ["#333", "#EEE", level];
  return (
    <Chip label={label} size="small"
      sx={{ fontWeight: 700, fontSize: 11, height: 24, borderRadius: 3, color: fg, bgcolor: bg }} />
  );
}

function CronPanel() {
  const [cron,    setCron]    = useState<CronState | null>(null);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [ok,      setOk]      = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | "enable" | "disable" | "run">(null);
  const [rateInput, setRateInput] = useState<string>("");

  async function refresh() {
    try {
      const c = await getCronState();
      setCron(c);
      // El selector muestra el CICLO configurado; en modo recuperación el rate vivo
      // de la regla es el escalón del backoff, no lo que eligió el usuario.
      const cycle = c.cycle_minutes ?? c.rate_minutes;
      if (cycle != null) setRateInput(String(cycle));
    }
    catch (e: any) { setErr(String(e?.message ?? e)); }
  }
  useEffect(() => { refresh(); }, []);

  async function apply(action: "enable" | "disable" | "run") {
    setBusy(true); setErr(null); setOk(null);
    try {
      if (action === "run") {
        // Corrida manual inmediata: NO toca la programación del cron.
        await runScraperNow();
        setOk("Corrida disparada. El scraper tarda ~1-2 min; los resultados aparecen en la pestaña Corridas.");
      } else {
        const r = await setCronState(action);
        if (action === "enable" && r.next_run_at) {
          setOk(`Cron activado. Próxima corrida: ${fmtDate(r.next_run_at)}.`);
        }
        await refresh();
      }
      setConfirm(null);
    }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  const parsedRate  = /^\d+$/.test(rateInput.trim()) ? parseInt(rateInput.trim(), 10) : null;
  const rateChanged = parsedRate != null && parsedRate !== (cron?.cycle_minutes ?? cron?.rate_minutes);

  async function applyRate() {
    if (parsedRate == null) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await setCronRate(parsedRate);
      setOk(r.next_run_at
        ? `Ciclo cambiado a ${parsedRate} min. Próxima corrida: ${fmtDate(r.next_run_at)}.`
        : `Ciclo cambiado a ${parsedRate} min. El cron está detenido: rige al activarlo.`);
      await refresh();
    }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  const enabled = cron?.state === "ENABLED";
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Cron del scraper (AWS)</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Si algo falla, detené AWS y prendé el Windows box (y viceversa). BIND es de sesión única — nunca los dos juntos.
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }} />
      {!cron ? <CircularProgress size={20} /> : !cron.configured ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>cron no configurado</Typography>
      ) : (
        <>
          {cron.backoff_minutes != null && (
            <Tooltip title={`Se detectaron transferencias perdidas → el ciclo se aceleró solo a 5 min y sube de a 5 por corrida sin novedades hasta volver al configurado.${cron.backoff_since ? ` Desde ${fmtDate(cron.backoff_since)}.` : ""}`}>
              <Chip label={`RECUPERACIÓN · cada ${cron.backoff_minutes} min`} size="small"
                sx={{ fontWeight: 700, color: "#9A6700", bgcolor: "#FFF6E0" }} />
            </Tooltip>
          )}
          {cron.rate_minutes != null ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>ciclo</Typography>
              <TextField size="small" select value={rateInput} disabled={busy}
                onChange={(e) => setRateInput(e.target.value)}
                sx={{ "& .MuiSelect-select": { py: 0.5, fontSize: 13 } }}>
                {CRON_RATE_OPTIONS.map((m) => (
                  <MenuItem key={m} value={String(m)}>{m} min</MenuItem>
                ))}
                {rateInput !== "" && !CRON_RATE_OPTIONS.includes(parseInt(rateInput, 10)) && (
                  // Valor legacy fuera del set fijo (seteado antes de esta versión):
                  // se muestra pero no se puede re-elegir.
                  <MenuItem value={rateInput} disabled>{rateInput} min</MenuItem>
                )}
              </TextField>
              {rateChanged && (
                <Button size="small" variant="outlined" disabled={busy} onClick={applyRate}
                  sx={{ minWidth: 0, px: 1 }}>Aplicar</Button>
              )}
            </Stack>
          ) : (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>{cron.schedule}</Typography>
          )}
          <Tooltip title="Refrescar estado">
            <span><IconButton size="small" onClick={refresh} disabled={busy}><RefreshIcon fontSize="small" /></IconButton></span>
          </Tooltip>
          <Tooltip title="Dispara una corrida del scraper YA, sin tocar la programación del cron.">
            <span>
              <Button variant="outlined" size="small" startIcon={<BoltIcon />}
                disabled={busy} onClick={() => setConfirm("run")}>Ejecutar ahora</Button>
            </span>
          </Tooltip>
          {/* El switch no cambia nada por sí solo: abre la confirmación (sesión
              única BIND) y el estado real se refresca después de aplicar. */}
          <Tooltip title={enabled ? "Detener el cron del scraper" : "Activar el cron del scraper"}>
            <FormControlLabel sx={{ ml: 0.5, mr: 0 }}
              control={<Switch checked={enabled} color="success" disabled={busy}
                onChange={() => setConfirm(enabled ? "disable" : "enable")} />}
              label={<Typography variant="caption"
                sx={{ fontWeight: 700, color: enabled ? "#166534" : "#6B7280" }}>
                {enabled ? "ACTIVO" : "DETENIDO"}
              </Typography>} />
          </Tooltip>
        </>
      )}
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ width: "100%" }}>{err}</Alert>}
      {ok  && <Alert severity="success" onClose={() => setOk(null)} sx={{ width: "100%" }}>{ok}</Alert>}

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>
          {confirm === "enable" ? "Activar el cron de AWS"
            : confirm === "disable" ? "Detener el cron de AWS"
            : "Ejecutar el scraper ahora"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm === "enable" ? (
              <>Vas a <b>activar</b> el scraper de AWS (corre cada
                {" "}{cron?.cycle_minutes ?? cron?.rate_minutes ?? "?"} min). Antes de continuar,
                asegurate de que el <b>Windows box esté apagado</b> — BIND permite una sola sesión
                concurrente y, si los dos corren, se bloquean mutuamente. ¿Continuar?</>
            ) : confirm === "disable" ? (
              <>Vas a <b>detener</b> el scraper de AWS. Después de esto podés prender el
                <b> Windows box</b> sin riesgo de colisión de sesión. La conciliación de AWS deja
                de correr hasta que lo reactives. ¿Continuar?</>
            ) : (
              <>Vas a disparar <b>una corrida del scraper ahora mismo</b>. La programación del
                cron <b>no se toca</b>: las corridas programadas siguen igual. Recordá que BIND es
                de <b>sesión única</b> — si el Windows box está corriendo en este momento, pueden
                colisionar. ¿Continuar?</>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancelar</Button>
          <Button onClick={() => apply(confirm!)} disabled={busy}
            variant="contained"
            color={confirm === "enable" ? "success" : confirm === "disable" ? "error" : "primary"}>
            {busy ? "Aplicando…"
              : confirm === "enable" ? "Activar"
              : confirm === "disable" ? "Detener"
              : "Ejecutar ahora"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

const CRED_ALERT_LABEL: Record<string, string> = {
  password_expired: "CLAVE EXPIRADA",
  user_locked:      "USUARIO BLOQUEADO",
};

function CredencialesPanel() {
  const [creds, setCreds] = useState<ScraperCredsState | null>(null);
  const [err,   setErr]   = useState<string | null>(null);
  const [ok,    setOk]    = useState<string | null>(null);

  const [dlgOpen,  setDlgOpen]  = useState(false);
  const [pass,     setPass]     = useState("");
  const [pass2,    setPass2]    = useState("");
  const [busy,     setBusy]     = useState(false);
  const [dlgErr,   setDlgErr]   = useState<string | null>(null);

  async function refresh() {
    try { setCreds(await getScraperCreds()); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
  }
  useEffect(() => { refresh(); }, []);

  function openDialog() {
    setPass(""); setPass2(""); setDlgErr(null); setBusy(false); setDlgOpen(true);
  }

  async function submit() {
    setBusy(true); setDlgErr(null);
    try {
      await updateScraperCreds(pass);
      setDlgOpen(false);
      setOk("Clave validada contra BIND y guardada. El scraper se recupera solo en la próxima corrida.");
      await refresh();
    } catch (e: any) {
      setDlgErr(String(e?.message ?? e));
    } finally { setBusy(false); }
  }

  const alertLabel = creds?.alert ? (CRED_ALERT_LABEL[creds.alert.cause] ?? "LOGIN FALLANDO") : null;
  const canSubmit = pass.length > 0 && pass === pass2 && !busy;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Credenciales BIND scraper</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          BIND vence la clave cada ~75-90 días. Cuando expira: renovarla en id.bind.com.ar y cargarla acá — sin tocar AWS.
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }} />
      {!creds ? <CircularProgress size={20} /> : (
        <>
          {alertLabel && (
            <Tooltip title={`Detectada ${fmtDate(creds.alert?.first_detected_at)} · último aviso ${fmtDate(creds.alert?.last_alerted_at)} (se repite máx 1 vez/día). El scraper no descarga extractos hasta cargar la clave nueva.`}>
              <Chip label={alertLabel} size="small"
                sx={{ fontWeight: 700, color: "#B91C1C", bgcolor: "#FEE2E2" }} />
            </Tooltip>
          )}
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{creds.username ?? "—"}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              último cambio {fmtDate(creds.last_changed_at ?? creds.secret_last_changed_at)}
              {creds.last_changed_by ? ` · ${creds.last_changed_by}` : ""}
            </Typography>
          </Box>
          <Button variant={alertLabel ? "contained" : "outlined"} color={alertLabel ? "error" : "primary"}
            size="small" startIcon={<KeyIcon />} onClick={openDialog}>
            Actualizar clave
          </Button>
        </>
      )}
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ width: "100%" }}>{err}</Alert>}
      {ok  && <Alert severity="success" onClose={() => setOk(null)} sx={{ width: "100%" }}>{ok}</Alert>}

      <Dialog open={dlgOpen} onClose={busy ? undefined : () => setDlgOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Actualizar clave BIND del scraper
          <Typography variant="body2" color="text.secondary">
            usuario {creds?.username ?? "—"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Primero renovala en <b>id.bind.com.ar</b>. Al guardar se valida con <b>un login real</b> contra
              BIND: si el login falla, no se guarda nada. Si justo hay una corrida del scraper en curso puede
              fallar por sesión única — reintentá en unos minutos.
            </Alert>
            <TextField label="Clave nueva" type="password" value={pass} autoComplete="new-password"
              onChange={(e) => setPass(e.target.value)} disabled={busy} autoFocus />
            <TextField label="Repetir clave nueva" type="password" value={pass2} autoComplete="new-password"
              onChange={(e) => setPass2(e.target.value)} disabled={busy}
              error={pass2.length > 0 && pass2 !== pass}
              helperText={pass2.length > 0 && pass2 !== pass ? "no coincide" : " "} />
            {dlgErr && <Alert severity="error">{dlgErr}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)} disabled={busy}>Cancelar</Button>
          <Button onClick={submit} variant="contained" disabled={!canSubmit}>
            {busy ? "Validando contra BIND…" : "Validar y guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

function uptimeColor(p: number | null | undefined): string {
  return p == null ? "#6B7280" : p >= 99.9 ? "#166534" : p >= 99 ? "#9A6700" : "#B91C1C";
}

function UptimePanel() {
  const [up,   setUp]   = useState<Uptime | null>(null);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState<string | null>(null);

  async function refresh() {
    setBusy(true); setErr(null);
    try { setUp(await getUptime()); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { refresh(); }, []);

  const pct = (n: number | null | undefined) => (n == null ? "—" : `${n.toFixed(n >= 99.9 ? 3 : 2)}%`);
  const desde = up?.desde
    ? new Date(up.desde).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
    : null;

  const windows: { key: "hora" | "dia" | "mes"; label: string }[] = [
    { key: "hora", label: "Última hora" },
    { key: "dia",  label: "Últimas 24 h" },
    { key: "mes",  label: "Últimos 30 días" },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Uptime de webhooks</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            % de transferencias cuyo webhook llegó bien vs. las que no llegaron (se re-dispararon).
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {!up && busy ? <CircularProgress size={22} /> : up && (
          <Tooltip title="Refrescar">
            <span><IconButton size="small" onClick={refresh} disabled={busy}><RefreshIcon fontSize="small" /></IconButton></span>
          </Tooltip>
        )}
      </Stack>

      {up && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {windows.map(({ key, label }) => {
            const w = up.windows?.[key];
            return (
              <Box key={key} sx={{ minWidth: 150, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15, color: uptimeColor(w?.uptime_pct) }}>
                  {w && w.total > 0 ? pct(w.uptime_pct) : "—"}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {w && w.total > 0 ? `${w.fallidas} fallidas de ${w.total}` : "sin transferencias"}
                </Typography>
              </Box>
            );
          })}
          <Box sx={{ minWidth: 190, p: 1.5, borderRadius: 2, bgcolor: "rgba(0,0,0,0.03)" }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
              Histórico{desde ? ` (desde ${desde})` : ""}
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15, color: uptimeColor(up.uptime_pct) }}>
              {pct(up.uptime_pct)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {up.fallidas} fallidas de {up.total} ({pct(up.fallidas_pct)})
            </Typography>
          </Box>
        </Stack>
      )}

      {up && up.by_account.length > 1 && (
        <Stack direction="row" spacing={3} sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          {up.by_account.map((a) => (
            <Typography key={a.account} variant="caption" sx={{ color: "text.secondary" }}>
              <b>Cta {a.account}</b> (histórico): {pct(a.uptime_pct)} uptime · {a.fallidas}/{a.total} fallidas
            </Typography>
          ))}
        </Stack>
      )}
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mt: 1.5 }}>{err}</Alert>}
    </Paper>
  );
}

function AlertsView() {
  const [rows,    setRows]    = useState<BindAlert[]>([]);
  const [cursor,  setCursor]  = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [level,   setLevel]   = useState<"" | AlertLevel>("");

  async function load(append = false) {
    setLoading(true); setErr(null);
    try {
      const r = await listAlerts({ level: level || undefined, limit: 100, cursor: append ? cursor ?? undefined : undefined });
      setRows(append ? [...rows, ...r.items] : r.items);
      setCursor(r.cursor);
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  const hasMore = !!cursor;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" select label="Nivel" value={level}
          onChange={(e) => setLevel(e.target.value as any)} sx={{ minWidth: 140 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="error">Error</MenuItem>
          <MenuItem value="warning">Warning</MenuItem>
          <MenuItem value="info">Info</MenuItem>
        </TextField>
        <Tooltip title="Refresh">
          <span><IconButton onClick={() => load(false)} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
      </Stack>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Log de alertas del pipeline de extracción/conciliación BIND — equivalente a lo que se envía por email a operaciones (pga@ y jab@).
      </Typography>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Fecha / Hora</TableCell>
              <TableCell>Nivel</TableCell>
              <TableCell>Origen</TableCell>
              <TableCell>Cuenta</TableCell>
              <TableCell>Mensaje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                Sin alertas registradas.
              </TableCell></TableRow>
            )}
            {rows.map((a, i) => (
              <TableRow key={`${a.ts}#${i}`} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(a.ts)}</TableCell>
                <TableCell><LevelChip level={a.level} /></TableCell>
                <TableCell>{a.source}</TableCell>
                <TableCell>{a.account ?? "—"}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{a.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {hasMore && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Button onClick={() => load(true)} disabled={loading} variant="outlined" size="small">
            {loading ? "Cargando…" : "Cargar más"}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export default function AgExtracto() {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>AG-Extracto</Typography>
      <UptimePanel />
      <CronPanel />
      <CredencialesPanel />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Alertas" />
        <Tab label="Corridas" />
      </Tabs>
      {tab === 0 ? <AlertsView /> : <RunsView />}
    </Box>
  );
}
